<template>
  <div class="promo-mail-list-page">
    <LoadingOverlay :show="busy" :text="busyText" />

    <div class="pml-header">
      <div class="pml-title-area">
        <h2 class="pml-page-title">홍보</h2>
      </div>
    </div>

    <div class="pml-tabs" role="tablist">
      <button
        type="button"
        class="pml-tab"
        :class="{ 'is-active': mainTab === 'stats' }"
        role="tab"
        @click="switchToStats"
      >
        통계분석
      </button>
      <button
        type="button"
        class="pml-tab"
        :class="{ 'is-active': mainTab === 'landing' }"
        role="tab"
        @click="switchToLanding"
      >
        랜딩 HTML
      </button>
      <button
        type="button"
        class="pml-tab"
        :class="{ 'is-active': mainTab === 'mail' }"
        role="tab"
        @click="switchToMail"
      >
        수신목록
      </button>
    </div>

    <p v-if="errorMessage" class="pml-error">{{ errorMessage }}</p>

    <!-- ── 수신목록 ── -->
    <template v-if="mainTab === 'mail'">
      <div class="pml-header-actions pml-header-actions--inline">
        <v-btn variant="flat" class="btn-refresh" :disabled="busy" @click="openCaionModal">
          카이온 고객 이메일
        </v-btn>
        <v-btn variant="flat" class="btn-refresh" :disabled="busy" @click="loadStats">
          다시 집계
        </v-btn>
        <v-btn
          variant="flat"
          class="btn-save"
          :disabled="busy || stats.uniqueEmails < 1"
          @click="downloadExcel"
        >
          엑셀 다운로드
        </v-btn>
      </div>

      <section class="pml-stats" aria-label="수신목록 집계">
        <div class="pml-stat">
          <span class="pml-stat-n">{{ formatNum(stats.crawlWithEmail) }}</span>
          <span class="pml-stat-l">크롤 이메일 행</span>
        </div>
        <div class="pml-stat pml-stat--accent">
          <span class="pml-stat-n">{{ formatNum(stats.uniqueEmails) }}</span>
          <span class="pml-stat-l">받을 고유 이메일</span>
        </div>
        <button type="button" class="pml-stat pml-stat--click" @click="openCaionModal">
          <span class="pml-stat-n">{{ formatNum(stats.excludedCaionRows) }}</span>
          <span class="pml-stat-l">카이온 회원 제외</span>
          <span class="pml-stat-hint">회원 {{ formatNum(stats.caionMemberCount) }}곳 · 이메일 보기</span>
        </button>
        <div class="pml-stat">
          <span class="pml-stat-n">{{ formatNum(stats.excludedFnaRows) }}</span>
          <span class="pml-stat-l">FNA 회원 제외</span>
          <span class="pml-stat-hint">
            <template v-if="stats.fnaAvailable">회원 {{ formatNum(stats.fnaMemberCount) }}곳</template>
            <template v-else>FNA 조회 불가</template>
          </span>
        </div>
      </section>

      <p v-if="stats.fnaError" class="pml-warn">
        FNA(스텝유) 회원 제외에 실패했습니다: {{ stats.fnaError }}
      </p>

      <section class="pml-note">
        <p>
          엑셀 열은 <strong>이메일주소</strong>, <strong>이름</strong> 순서입니다.
          같은 이메일이 여러 기관이면 이름을 쉼표로 묶어 한 행으로 둡니다. 발송 기능은 없습니다.
        </p>
      </section>
    </template>

    <!-- ── 통계분석 ── -->
    <template v-else-if="mainTab === 'stats'">
      <div class="pml-header-actions pml-header-actions--inline">
        <v-btn variant="flat" class="btn-save" :disabled="busy" @click="openStatsFolder">
          통계 폴더 올리기
        </v-btn>
        <input
          v-model="newGroupName"
          type="text"
          class="pml-new-folder"
          maxlength="80"
          placeholder="새 폴더 이름"
          @keyup.enter="createUserGroup"
        />
        <v-btn
          variant="outlined"
          class="btn-html"
          :disabled="busy || !newGroupName.trim()"
          @click="createUserGroup"
        >
          폴더 만들기
        </v-btn>
      </div>
      <input
        ref="statsFolderInputRef"
        class="pml-folder-input"
        type="file"
        multiple
        webkitdirectory
        @change="onStatsFolder"
      />
      <p v-if="!statsReports.length && !userGroups.length" class="pml-empty">아직 올린 폴더가 없습니다.</p>
      <template v-else>
        <section class="pml-folder-list" aria-label="저장된 폴더">
          <div class="pml-folder-list-head">
            <h3 class="pml-folder-list-title">저장된 폴더 {{ statsReports.length }}개</h3>
            <div class="pml-folder-tools">
              <label class="pml-check-all">
                <input
                  type="checkbox"
                  :checked="allChecked"
                  :indeterminate="someChecked && !allChecked"
                  @change="toggleCheckAll"
                />
                전체 선택
              </label>
              <v-btn
                variant="flat"
                class="btn-save"
                :disabled="busy || !checkedFolders.length"
                @click="runGroupAnalysis"
              >
                분석하기{{ checkedFolders.length ? ` (${checkedFolders.length})` : '' }}
              </v-btn>
              <v-btn
                variant="outlined"
                class="btn-delete"
                :disabled="busy || !checkedFolders.length"
                @click="deleteChecked"
              >
                선택 삭제{{ checkedFolders.length ? ` (${checkedFolders.length})` : '' }}
              </v-btn>
            </div>
          </div>
          <ul
            class="pml-folder-tree"
            :class="{ 'is-drop-root': dropHover === 'root', 'is-takeout': draggingGrouped }"
            @dragover="onDragOverOut"
            @dragleave="onDragLeaveOut"
            @drop.prevent="onDropRoot"
          >
            <li
              v-for="group in userGroups"
              :key="`g-${group.idx}`"
              class="pml-user-folder"
              :class="{ 'is-drop': dropHover === group.idx }"
              @dragover="onDragOverFolder($event, group.idx)"
              @dragleave="onDragLeaveFolder($event, group.idx)"
              @drop.prevent.stop="onDropFolder(group.idx)"
            >
              <div class="pml-folder-item is-group">
                <button
                  type="button"
                  class="pml-folder-twist"
                  :aria-label="isGroupOpen(group.idx) ? '접기' : '펼치기'"
                  @click="toggleGroupOpen(group.idx)"
                >
                  <v-icon size="16">{{ isGroupOpen(group.idx) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                </button>
                <input
                  type="checkbox"
                  class="pml-folder-check"
                  :checked="groupAllChecked(group.idx)"
                  :disabled="!groupItems(group.idx).length"
                  :aria-label="`${group.name} 안 항목 선택`"
                  @change="toggleGroupCheck(group.idx)"
                />
                <v-icon size="18" class="pml-folder-icon">mdi-folder</v-icon>
                <template v-if="editingGroup === group.idx">
                  <input
                    v-model="editingName"
                    type="text"
                    class="pml-rename-input"
                    maxlength="80"
                    @keyup.enter="saveGroupName(group.idx)"
                  />
                  <button type="button" class="pml-mini-btn" @click="saveGroupName(group.idx)">저장</button>
                  <button type="button" class="pml-mini-btn" @click="editingGroup = 0">취소</button>
                </template>
                <template v-else>
                  <span class="pml-folder-name">{{ group.name }}</span>
                  <span class="pml-folder-time">{{ groupItems(group.idx).length }}개</span>
                  <button type="button" class="pml-mini-btn" @click="startRename(group)">이름</button>
                  <button type="button" class="pml-mini-btn is-danger" @click="removeUserGroup(group)">삭제</button>
                </template>
              </div>
              <ul v-if="isGroupOpen(group.idx) && groupItems(group.idx).length" class="pml-folder-children">
                <li
                  v-for="(item, index) in groupItems(group.idx)"
                  :key="item.folder"
                  class="pml-folder-item"
                  :class="{
                    'is-selected': selectedFolder === item.folder,
                    'is-dragging': dragNames.includes(item.folder),
                  }"
                  draggable="true"
                  @dragstart="onItemDragStart($event, item.folder)"
                  @dragend="onItemDragEnd"
                >
                  <input
                    v-model="checkedFolders"
                    type="checkbox"
                    class="pml-folder-check"
                    :value="item.folder"
                    :aria-label="`${promoMailLabel(item.folder)} 선택`"
                    @click.stop
                  />
                  <v-icon size="18" class="pml-mail-icon">mdi-email-outline</v-icon>
                  <button type="button" class="pml-folder-open" @click="selectFolder(item.folder)">
                    <span class="pml-folder-index">{{ index + 1 }}</span>
                    <span class="pml-folder-name">{{ promoMailLabel(item.folder) }}</span>
                    <span v-if="item.savedAt" class="pml-folder-time">{{ formatDate(item.savedAt) }}</span>
                  </button>
                  <button type="button" class="pml-out-btn" aria-label="빼기" @click.stop="takeOut(item.folder)">
                    <v-icon size="16">mdi-arrow-left</v-icon>
                  </button>
                </li>
              </ul>
            </li>
            <li
              v-for="(item, index) in looseItems"
              :key="item.folder"
              class="pml-folder-item is-loose"
              :class="{
                'is-selected': selectedFolder === item.folder,
                'is-dragging': dragNames.includes(item.folder),
              }"
              draggable="true"
              @dragstart="onItemDragStart($event, item.folder)"
              @dragend="onItemDragEnd"
            >
              <span class="pml-folder-twist" aria-hidden="true" />
              <input
                v-model="checkedFolders"
                type="checkbox"
                class="pml-folder-check"
                :value="item.folder"
                :aria-label="`${promoMailLabel(item.folder)} 선택`"
                @click.stop
              />
              <v-icon size="18" class="pml-mail-icon">mdi-email-outline</v-icon>
              <button type="button" class="pml-folder-open" @click="selectFolder(item.folder)">
                <span class="pml-folder-index">{{ index + 1 }}</span>
                <span class="pml-folder-name">{{ promoMailLabel(item.folder) }}</span>
                <span v-if="item.savedAt" class="pml-folder-time">{{ formatDate(item.savedAt) }}</span>
              </button>
            </li>
          </ul>
        </section>

        <div v-if="selectedReport && selectedReport.htmlFiles.length" class="pml-html-toggle">
          <v-btn variant="outlined" class="btn-html" @click="htmlOpen = !htmlOpen">
            {{ htmlOpen ? '메일 HTML 닫기' : '메일 HTML 열기' }}
          </v-btn>
        </div>
        <template v-if="htmlOpen && selectedReport">
          <iframe
            v-for="html in selectedReport.htmlFiles"
            :key="`${selectedReport.folder}-${html.name}`"
            class="pml-html-frame"
            sandbox=""
            :srcdoc="html.html"
            :title="html.name"
          />
        </template>

        <section v-if="shown && meeting" class="pml-group-report pml-paper" aria-label="통계분석 보고서">
          <header class="pml-paper-head">
            <h3>
              메일 {{ formatNum(shown.count) }}통 · 발송 {{ formatNum(shown.sentOk) }}건
              <template v-if="meeting.mails.length">
                · {{ koMonthDay(meeting.mails[0].sentAt) }}
                <template v-if="meeting.mails.length > 1"> ~ {{ koMonthDay(meeting.mails[meeting.mails.length - 1].sentAt) }}</template>
              </template>
              통계분석 보고서
            </h3>
          </header>

          <section class="pml-chapter">
            <h4><span class="pml-num">01</span> 보낸 메일</h4>
            <table class="pml-meet-table">
              <thead>
                <tr>
                  <th>메일</th>
                  <th>방향</th>
                  <th>발송일</th>
                  <th class="is-num">발송</th>
                  <th class="is-num">오픈율</th>
                  <th class="is-num">클릭율</th>
                  <th class="is-num">유입 구매율</th>
                  <th class="is-num">구매</th>
                  <th class="is-num">구매 금액</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in meeting.mails"
                  :key="row.folder"
                  :class="{ 'is-lead': row.folder === meeting.winnerFolder }"
                >
                  <td>{{ row.short }}</td>
                  <td>{{ row.angle }}</td>
                  <td>{{ koMonthDay(row.sentAt) }}</td>
                  <td class="is-num">{{ formatNum(row.values[0]) }}</td>
                  <td class="is-num">{{ row.openRate.toFixed(1) }}%</td>
                  <td class="is-num">{{ row.clickRate.toFixed(1) }}%</td>
                  <td class="is-num">
                    {{ clickEntryRate(row).toFixed(1) }}%
                    <span class="pml-rate-sub">{{ formatNum(row.clickBuyers) }}/{{ formatNum(row.clicks) }}</span>
                  </td>
                  <td class="is-num">{{ formatNum(row.afterSendOrders) }}건</td>
                  <td class="is-num">{{ formatNum(row.afterSendAmount) }}원</td>
                </tr>
              </tbody>
            </table>
            <p class="pml-footnote">발송 이후 결제는 그 직전 마지막 메일에 넣습니다. 유입 구매율은 링크를 누른 사람 중 그 뒤에 결제한 비율입니다.</p>
          </section>

          <section v-if="meeting.timeline.length" class="pml-chapter">
            <h4><span class="pml-num">02</span> 발송&결제 타임라인</h4>
            <div class="pml-tl">
              <article v-for="span in meeting.timeline" :key="`tl-${span.name}`" class="pml-tl-span">
                <header class="pml-tl-send">
                  <time>{{ koMonthDay(span.sentAt) }} {{ span.sentAt.slice(11, 16) }}</time>
                  <strong>{{ span.short }}</strong>
                </header>
                <div
                  v-for="day in span.days"
                  :key="`${span.name}-${day.day}`"
                  class="pml-tl-day"
                  :class="{ 'is-quiet': !day.orders }"
                >
                  <span class="pml-tl-when">
                    {{ koMonthDay(day.day) }}
                    <small v-if="day.note">{{ day.note }}</small>
                  </span>
                  <div class="pml-tl-track">
                    <div
                      class="pml-tl-fill"
                      :class="{ 'is-peak': day.amount > 0 && day.amount === meeting.dayMax }"
                      :style="{ width: day.amount ? `${(day.amount / meeting.dayMax) * 100}%` : '0%' }"
                    />
                  </div>
                  <span class="pml-tl-money">
                    <template v-if="day.orders">{{ formatNum(day.orders) }}건 · {{ formatNum(day.amount) }}원</template>
                    <template v-else>결제 없음</template>
                  </span>
                </div>
                <footer
                  class="pml-tl-total"
                  :class="{ 'is-lead': span.amount > 0 && span.amount === meeting.spanMax }"
                >
                  <span>합계</span>
                  <strong>{{ formatNum(span.orders) }}건</strong>
                  <strong>{{ formatNum(span.amount) }}원</strong>
                </footer>
              </article>
            </div>
            <p class="pml-footnote">같은 날 메일을 보낸 경우, 그 시각 전 결제는 이전 메일에 넣습니다.</p>
          </section>

          <section v-if="shown.products.length" class="pml-chapter">
            <h4><span class="pml-num">03</span> 팔린 상품</h4>
            <table class="pml-meet-table">
              <thead>
                <tr>
                  <th>상품명</th>
                  <th class="is-num">주문</th>
                  <th class="is-num">수량</th>
                  <th class="is-num">판매금액</th>
                  <th class="is-num">비중</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="product in productTableRows" :key="`pt-${product.name}`">
                  <td>{{ product.name }}</td>
                  <td class="is-num">{{ formatNum(product.orders) }}</td>
                  <td class="is-num">{{ formatNum(product.qty) }}</td>
                  <td class="is-num">{{ formatNum(product.amount) }}원</td>
                  <td class="is-num">{{ formatPct(product.amount, shown.productAmount) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>합계</td>
                  <td class="is-num">{{ formatNum(productTotals.orders) }}건</td>
                  <td class="is-num">{{ formatNum(productTotals.qty) }}</td>
                  <td class="is-num">{{ formatNum(productTotals.amount) }}원</td>
                  <td class="is-num">100%</td>
                </tr>
              </tfoot>
            </table>
            <button
              v-if="shown.products.length > 10"
              type="button"
              class="pml-more-btn"
              @click="productShowAll = !productShowAll"
            >
              {{ productShowAll ? '상위 10개만 보기' : `전체 보기 (${shown.products.length}개)` }}
            </button>
          </section>

          <section v-if="shown.regions.length" class="pml-chapter">
            <h4><span class="pml-num">04</span> 지역</h4>
            <div class="pml-map-grid">
              <svg viewBox="0 0 400 510" class="pml-map" role="img" aria-label="지역별 오픈율, 클릭율, 구매액">
                <g v-for="region in KOREA_REGIONS" :key="region.name">
                  <path
                    class="pml-map-region"
                    :d="region.d"
                    :fill="regionFill(region.name)"
                    @mouseenter="showTip($event, regionTipLines(region.name))"
                    @mousemove="moveTip"
                    @mouseleave="hideTip"
                  />
                  <text class="pml-map-value" :x="region.lx" :y="region.ly - 8" :fill="regionTextColor(region.name)">오픈 {{ regionRateText(region.name, 'open') }}</text>
                  <text class="pml-map-value" :x="region.lx" :y="region.ly + 4" :fill="regionTextColor(region.name)">클릭 {{ regionRateText(region.name, 'click') }}</text>
                  <text class="pml-map-money" :x="region.lx" :y="region.ly + 16" :fill="regionTextColor(region.name)">{{ regionMoneyText(region.name) }}</text>
                </g>
              </svg>
            </div>
            <div class="pml-region-table-head">
              <div class="pml-metric-toggle">
                <button
                  type="button"
                  class="pml-metric-btn"
                  :class="{ 'is-active': regionView === 'count' }"
                  @click="setRegionView('count')"
                >건수</button>
                <button
                  type="button"
                  class="pml-metric-btn"
                  :class="{ 'is-active': regionView === 'rate' }"
                  @click="setRegionView('rate')"
                >비율</button>
              </div>
              <p class="pml-footnote">비율은 발송 건수 대비입니다. 발송이 많은 지역과 반응이 좋은 지역을 구분합니다.</p>
            </div>
            <table class="pml-meet-table">
              <thead>
                <tr v-if="regionView === 'count'">
                  <th>
                    <button type="button" class="pml-sort" :class="{ 'is-active': regionSortKey === 'name' }" @click="sortRegion('name')">
                      지역<span v-if="regionSortKey === 'name'">{{ regionSortDesc ? '↓' : '↑' }}</span>
                    </button>
                  </th>
                  <th class="is-num">
                    <button type="button" class="pml-sort" :class="{ 'is-active': regionSortKey === 'sentOk' }" @click="sortRegion('sentOk')">
                      발송<span v-if="regionSortKey === 'sentOk'">{{ regionSortDesc ? '↓' : '↑' }}</span>
                    </button>
                  </th>
                  <th class="is-num">
                    <button type="button" class="pml-sort" :class="{ 'is-active': regionSortKey === 'opened' }" @click="sortRegion('opened')">
                      오픈<span v-if="regionSortKey === 'opened'">{{ regionSortDesc ? '↓' : '↑' }}</span>
                    </button>
                  </th>
                  <th class="is-num">
                    <button type="button" class="pml-sort" :class="{ 'is-active': regionSortKey === 'clicked' }" @click="sortRegion('clicked')">
                      클릭<span v-if="regionSortKey === 'clicked'">{{ regionSortDesc ? '↓' : '↑' }}</span>
                    </button>
                  </th>
                  <th class="is-num">
                    <button type="button" class="pml-sort" :class="{ 'is-active': regionSortKey === 'amount' }" @click="sortRegion('amount')">
                      구매 금액<span v-if="regionSortKey === 'amount'">{{ regionSortDesc ? '↓' : '↑' }}</span>
                    </button>
                  </th>
                </tr>
                <tr v-else>
                  <th>
                    <button type="button" class="pml-sort" :class="{ 'is-active': regionSortKey === 'name' }" @click="sortRegion('name')">
                      지역<span v-if="regionSortKey === 'name'">{{ regionSortDesc ? '↓' : '↑' }}</span>
                    </button>
                  </th>
                  <th class="is-num">
                    <button type="button" class="pml-sort" :class="{ 'is-active': regionSortKey === 'openRate' }" @click="sortRegion('openRate')">
                      오픈율<span v-if="regionSortKey === 'openRate'">{{ regionSortDesc ? '↓' : '↑' }}</span>
                    </button>
                  </th>
                  <th class="is-num">
                    <button type="button" class="pml-sort" :class="{ 'is-active': regionSortKey === 'clickRate' }" @click="sortRegion('clickRate')">
                      클릭율<span v-if="regionSortKey === 'clickRate'">{{ regionSortDesc ? '↓' : '↑' }}</span>
                    </button>
                  </th>
                  <th class="is-num">
                    <button type="button" class="pml-sort" :class="{ 'is-active': regionSortKey === 'amount' }" @click="sortRegion('amount')">
                      총 구매액<span v-if="regionSortKey === 'amount'">{{ regionSortDesc ? '↓' : '↑' }}</span>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in regionRows" :key="`rt-${row.name}`">
                  <td>{{ row.name }}</td>
                  <template v-if="regionView === 'count'">
                    <td class="is-num">{{ formatNum(row.sentOk) }}</td>
                    <td class="is-num">{{ formatNum(row.opened) }}</td>
                    <td class="is-num">{{ formatNum(row.clicked) }}</td>
                    <td class="is-num">{{ formatNum(row.amount) }}원</td>
                  </template>
                  <template v-else>
                    <td class="is-num">{{ regionOpenRate(row).toFixed(1) }}%</td>
                    <td class="is-num">{{ regionClickRate(row).toFixed(1) }}%</td>
                    <td class="is-num">{{ formatNum(row.amount) }}원</td>
                  </template>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>{{ regionView === 'count' ? '합계' : '전체' }}</td>
                  <template v-if="regionView === 'count'">
                    <td class="is-num">{{ formatNum(regionTotals.sentOk) }}</td>
                    <td class="is-num">{{ formatNum(regionTotals.opened) }}</td>
                    <td class="is-num">{{ formatNum(regionTotals.clicked) }}</td>
                    <td class="is-num">{{ formatNum(regionTotals.amount) }}원</td>
                  </template>
                  <template v-else>
                    <td class="is-num">{{ regionTotals.openRate.toFixed(1) }}%</td>
                    <td class="is-num">{{ regionTotals.clickRate.toFixed(1) }}%</td>
                    <td class="is-num">{{ formatNum(regionTotals.amount) }}원</td>
                  </template>
                </tr>
              </tfoot>
            </table>
          </section>

          <!-- 공용 툴팁 (hover 즉시 표시) -->
          <div
            v-if="tooltip.show"
            class="pml-tooltip"
            :style="{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }"
          >
            <div v-for="(line, i) in tooltip.lines" :key="i" :class="{ 'is-title': i === 0 }">
              {{ line }}
            </div>
          </div>
        </section>

      </template>
    </template>

    <!-- ── 랜딩 HTML ── -->
    <template v-else>
      <section class="pml-slot-grid" aria-label="랜딩 슬롯">
        <button
          v-for="slot in slots"
          :key="slot.slot"
          type="button"
          class="pml-slot-card"
          :class="{ 'is-selected': selectedSlot === slot.slot }"
          @click="selectedSlot = slot.slot"
        >
          <span class="pml-slot-path">/{{ slot.slot }}</span>
          <span class="pml-slot-meta">
            {{ slot.versionCount }}개 버전
            <template v-if="slot.active"> · 게시중</template>
            <template v-else> · 미게시</template>
          </span>
          <span class="pml-slot-title">
            {{ slot.active?.title || '업로드된 HTML 없음' }}
          </span>
        </button>
      </section>

      <section v-if="currentSlot" class="pml-landing-panel">
        <div class="pml-landing-head">
          <div>
            <h3 class="pml-landing-title">장기요양지원센터.kr/{{ currentSlot.slot }}</h3>
            <p v-if="currentSlot.slot === 'b'" class="pml-landing-sub">
              설명회 신청 페이지입니다. 메일 신청 버튼은 아래 링크로 연결합니다.
            </p>
            <p v-else class="pml-landing-sub">
              HTML을 올리면 바로 게시에 반영됩니다. 이전 버전은 목록에 남아 다시 게시할 수 있습니다.
            </p>
          </div>
          <a
            class="pml-open-link"
            :href="currentSlot.publicUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            새 창에서 열기
          </a>
        </div>

        <section v-if="currentSlot.slot === 'b'" class="pml-track" aria-label="설명회 신청 집계">
          <div class="pml-track-linkbox">
            <div>
              <p class="pml-track-kicker">메일 신청 링크</p>
              <p class="pml-track-url">https://장기요양지원센터.kr/b</p>
              <p class="pml-track-note">
                메일의 신청하기 버튼 주소를 이 링크로 두면, 누르는 즉시 설명회 신청 페이지로 이동합니다.
                들어온 사람과 신청은 아래에 쌓이고, 상담 신청 분류는 급식통상임금설명회신청입니다.
              </p>
            </div>
            <button type="button" class="pml-action-btn" @click="copyApplyLink">
              {{ applyLinkCopied ? '복사됨' : '링크 복사' }}
            </button>
          </div>

          <div class="pml-track-stats">
            <div>
              <span>클릭하여 들어온 횟수</span>
              <strong>{{ formatNum(landingStats?.clicks) }}</strong>
            </div>
            <div>
              <span>클릭한 사람</span>
              <strong>{{ formatNum(landingStats?.people) }}</strong>
            </div>
            <div>
              <span>신청 횟수</span>
              <strong>{{ formatNum(landingStats?.applies) }}</strong>
            </div>
          </div>

          <h4 class="pml-version-heading">클릭한 사람</h4>
          <p v-if="landingStatsError" class="pml-empty">{{ landingStatsError }}</p>
          <p v-else-if="!landingStats" class="pml-empty">집계를 불러오는 중...</p>
          <p v-else-if="!landingStats.clickers.length" class="pml-empty">아직 들어온 사람이 없습니다.</p>
          <div v-else class="pml-track-table-wrap">
            <table class="pml-track-table">
              <thead>
                <tr>
                  <th>누구</th>
                  <th>연락처</th>
                  <th>이메일</th>
                  <th>클릭 횟수</th>
                  <th>신청 횟수</th>
                  <th>최근 입장</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in landingStats.clickers" :key="row.visitor">
                  <td>{{ row.who }}</td>
                  <td>{{ row.phone || '—' }}</td>
                  <td>{{ row.email || '—' }}</td>
                  <td>{{ formatNum(row.clickCount) }}</td>
                  <td>{{ formatNum(row.applyCount) }}</td>
                  <td>{{ formatDate(row.lastAt) }}</td>
                  <td>
                    <button
                      v-if="isSuperAdmin"
                      type="button"
                      class="pml-action-btn pml-action-btn--danger"
                      :disabled="deletingVisitor === row.visitor"
                      @click="removeClicker(row)"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 class="pml-version-heading">신청 목록</h4>
          <p v-if="landingStats && !landingStats.applications.length" class="pml-empty">아직 신청이 없습니다.</p>
          <div v-else-if="landingStats?.applications.length" class="pml-track-table-wrap">
            <table class="pml-track-table">
              <thead>
                <tr>
                  <th>분류</th>
                  <th>기관</th>
                  <th>담당자</th>
                  <th>연락처</th>
                  <th>이메일</th>
                  <th>내용</th>
                  <th>신청일시</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in landingStats.applications" :key="row.id">
                  <td>{{ row.kind }}</td>
                  <td>
                    {{ row.orgName }}
                    <span v-if="row.orgCode" class="pml-track-sub">{{ row.orgCode }}</span>
                  </td>
                  <td>{{ row.contactName }}</td>
                  <td>{{ row.phone }}</td>
                  <td>{{ row.email || '—' }}</td>
                  <td class="pml-track-memo">{{ row.content }}</td>
                  <td>{{ formatDate(row.createdAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <div v-if="currentSlot.slot !== 'b'" class="pml-upload-box">
          <label class="pml-upload-label">
            <span>HTML 파일</span>
            <input
              ref="fileInputRef"
              type="file"
              accept=".html,.htm,text/html"
              class="pml-file-input"
              @change="onFileChange"
            />
          </label>
          <input
            v-model="uploadTitle"
            type="text"
            class="pml-title-input"
            maxlength="200"
            placeholder="관리용 제목 (선택)"
          />
          <v-btn
            variant="flat"
            class="btn-save"
            :disabled="busy || !pendingFile"
            @click="uploadHtml"
          >
            업로드 · 게시
          </v-btn>
          <span v-if="pendingFile" class="pml-file-name">{{ pendingFile.name }}</span>
        </div>

        <div v-if="currentSlot.slot !== 'b'" class="pml-version-list-wrap">
          <h4 class="pml-version-heading">버전 목록</h4>
          <p v-if="!currentSlot.versions.length" class="pml-empty">아직 올린 HTML이 없습니다.</p>
          <ul v-else class="pml-version-list">
            <li
              v-for="ver in currentSlot.versions"
              :key="ver.idx"
              class="pml-version-row"
              :class="{ 'is-active': ver.isActive }"
            >
              <div class="pml-version-main">
                <span class="pml-version-title">
                  {{ ver.title || ver.originalName || `버전 #${ver.idx}` }}
                  <span v-if="ver.isActive" class="pml-badge">게시중</span>
                </span>
                <span class="pml-version-meta">
                  #{{ ver.idx }}
                  · {{ ver.originalName || '-' }}
                  · {{ formatSize(ver.size) }}
                  · {{ formatDate(ver.uploadedAt) }}
                  <template v-if="ver.uploadedBy"> · {{ ver.uploadedBy }}</template>
                </span>
              </div>
              <div class="pml-version-actions">
                <button
                  v-if="!ver.isActive"
                  type="button"
                  class="pml-action-btn"
                  :disabled="busy"
                  @click="activateVersion(ver.idx)"
                >
                  게시
                </button>
                <button
                  type="button"
                  class="pml-action-btn"
                  :disabled="busy"
                  @click="downloadVersion(ver)"
                >
                  받기
                </button>
                <button
                  type="button"
                  class="pml-action-btn pml-action-btn--danger"
                  :disabled="busy"
                  @click="removeVersion(ver)"
                >
                  삭제
                </button>
              </div>
            </li>
          </ul>
        </div>
      </section>
    </template>

    <v-dialog v-model="caionModalOpen" max-width="720" scrollable>
      <div class="pml-modal">
        <div class="pml-modal-head">
          <div>
            <h3 class="pml-modal-title">카이온 고객 이메일</h3>
            <p class="pml-modal-sub">
              이메일 {{ formatNum(caionWithEmail) }}곳
              <template v-if="caionWithoutEmail">
                · 없음 {{ formatNum(caionWithoutEmail) }}곳
              </template>
            </p>
          </div>
          <button type="button" class="pml-modal-close" aria-label="닫기" @click="caionModalOpen = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>

        <div class="pml-modal-toolbar">
          <input
            v-model="caionSearch"
            type="search"
            class="pml-modal-search"
            placeholder="시설명·이메일 검색"
          />
          <button
            type="button"
            class="pml-copy-all-btn"
            :class="{ 'is-copied': copiedKey === 'all' }"
            :disabled="copyableEmails.length < 1"
            @click="copyAllEmails"
          >
            {{ copiedKey === 'all' ? '전체 복사됨' : '줄바꿈으로 전체 복사' }}
          </button>
        </div>

        <div v-if="caionLoading" class="pml-modal-empty">불러오는 중...</div>
        <div v-else-if="caionError" class="pml-modal-empty pml-modal-empty--error">{{ caionError }}</div>
        <div v-else-if="filteredCaionItems.length === 0" class="pml-modal-empty">
          {{ caionSearch.trim() ? '검색 결과가 없습니다.' : '카이온 고객이 없습니다.' }}
        </div>
        <ul v-else class="pml-email-list">
          <li
            v-for="(item, idx) in filteredCaionItems"
            :key="`${item.email || 'none'}-${item.name}-${idx}`"
            class="pml-email-row"
          >
            <div class="pml-email-main">
              <span class="pml-email-name">{{ item.name }}</span>
              <span class="pml-email-addr" :class="{ 'is-missing': !item.hasEmail }">
                {{ item.hasEmail ? item.email : '이메일 없음' }}
              </span>
            </div>
            <button
              type="button"
              class="pml-copy-btn"
              :class="{ 'is-copied': copiedKey === rowKey(item, idx) }"
              :disabled="!item.hasEmail"
              @click="copyEmail(item, idx)"
            >
              {{ copiedKey === rowKey(item, idx) ? '복사됨' : '복사' }}
            </button>
          </li>
        </ul>
      </div>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import { promoHtmlApi, promoMailListApi } from '@/services/api';
import {
  collectStibeeSavePayload,
  folderNameOf,
  mailProductNames,
  promoMailLabel,
  type CampaignEffectRow,
  type EffectMatchResult,
  type InstitutionSalesRow,
  type InstitutionSalesTotals,
  type RegionMatchResult,
  type StoredCampaignStats,
} from '@/utils/stibeeStatsText';

interface PromoMailListStats {
  crawlWithEmail: number;
  uniqueEmails: number;
  excludedCaionRows: number;
  excludedFnaRows: number;
  caionMemberCount: number;
  fnaMemberCount: number;
  fnaAvailable: boolean;
  fnaError?: string | null;
  invalid?: number;
}

interface CaionEmailItem {
  name: string;
  email: string;
  hasEmail: boolean;
  longtermCode?: string | null;
}

interface PromoHtmlVersion {
  idx: number;
  slot: string;
  title: string;
  originalName: string;
  size: number;
  isActive: boolean;
  uploadedBy: string;
  uploadedAt: string;
  publicUrl: string;
}

interface PromoHtmlSlot {
  slot: string;
  publicUrl: string;
  hasFile: boolean;
  versionCount: number;
  active: PromoHtmlVersion | null;
  versions: PromoHtmlVersion[];
}

const store = useStore();
const isSuperAdmin = computed(
  () => Number(store.getters.actualRoleLevel ?? store.getters.roleLevel ?? 0) >= 100,
);

const mainTab = ref<'mail' | 'stats' | 'landing'>('stats');
const busy = ref(false);
const busyText = ref('집계 중...');
const errorMessage = ref('');
const stats = ref<PromoMailListStats>({
  crawlWithEmail: 0,
  uniqueEmails: 0,
  excludedCaionRows: 0,
  excludedFnaRows: 0,
  caionMemberCount: 0,
  fnaMemberCount: 0,
  fnaAvailable: false,
  fnaError: null,
});

const caionModalOpen = ref(false);
const caionLoading = ref(false);
const caionError = ref('');
const caionItems = ref<CaionEmailItem[]>([]);
const caionWithEmail = ref(0);
const caionWithoutEmail = ref(0);
const caionSearch = ref('');
const copiedKey = ref('');
let copiedTimer: ReturnType<typeof setTimeout> | null = null;

const slots = ref<PromoHtmlSlot[]>([]);
const selectedSlot = ref('a');
const uploadTitle = ref('');
const pendingFile = ref<File | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const landingLoaded = ref(false);
const APPLY_LINK = 'https://장기요양지원센터.kr/b';

interface LandingClicker {
  visitor: string;
  who: string;
  phone: string;
  email: string;
  clickCount: number;
  applyCount: number;
  lastAt: string;
}

interface LandingApplication {
  id: number;
  kind: string;
  orgName: string;
  orgCode: string;
  contactName: string;
  phone: string;
  email: string;
  content: string;
  createdAt: string;
}

interface LandingStats {
  clicks: number;
  people: number;
  applies: number;
  clickers: LandingClicker[];
  applications: LandingApplication[];
}

const landingStats = ref<LandingStats | null>(null);
const landingStatsError = ref('');
const deletingVisitor = ref('');
const applyLinkCopied = ref(false);
let applyLinkTimer: ReturnType<typeof setTimeout> | null = null;
const mailLoaded = ref(false);
const statsFolderInputRef = ref<HTMLInputElement | null>(null);
const statsReports = ref<Array<{
  folder: string;
  savedAt: string;
  htmlFiles: Array<{ name: string; html: string }>;
  groupIdx: number | null;
  stats: StoredCampaignStats;
  region: RegionMatchResult;
  effectRow: CampaignEffectRow | null;
}>>([]);
const statsLoaded = ref(false);
const selectedFolder = ref('');
const checkedFolders = ref<string[]>([]);
const htmlOpen = ref(false);
const userGroups = ref<Array<{ idx: number; name: string }>>([]);
const openGroupIds = ref<number[]>([]);
const newGroupName = ref('');
const editingGroup = ref(0);
const editingName = ref('');
const dragNames = ref<string[]>([]);
const dropHover = ref<number | 'root' | null>(null);
let dropOpenTimer: ReturnType<typeof setTimeout> | null = null;

type GroupRegionRow = {
  name: string;
  sentOk: number;
  opened: number;
  clicked: number;
  orders: number;
  amount: number;
};
type GroupProductRow = {
  name: string;
  isMail: boolean;
  qty: number;
  orders: number;
  amount: number;
};
type GroupFolderRow = {
  folder: string;
  label: string;
  short: string;
  angle: string;
  sentAt: string;
  values: number[];
  openRate: number;
  clickRate: number;
  clicks: number;
  clickBuyers: number;
  effectAmount: number;
  lastClickAmount: number;
  lastClickOrders: number;
  afterSendAmount: number;
  afterSendOrders: number;
};

type StageRow = { label: string; buyers: number; orders: number; amount: number };
type NamedCount = { name: string; count: number };
type LinkRow = { url: string; clicks: number; dup: number };

type ReportView = {
  isGroup: boolean;
  title: string;
  periodNote: string;
  count: number;
  sentOk: number;
  sentFail: number;
  opened: number;
  clicked: number;
  unsub: number;
  buyers: number;
  orders: number;
  amount: number;
  avgOrder: number;
  cartOnly: number;
  boughtWithoutClick: { buyers: number; orders: number; amount: number };
  attributedAmount: number;
  attributedOrders: number;
  productMailAmount: number;
  productOtherAmount: number;
  introduced: string[];
  sameGoods: boolean;
  leads: {
    response: string;
    trend: string;
    region: string;
    stage: string;
    product: string;
    institution: string;
    mail: string;
  };
  failReasons: NamedCount[];
  links: LinkRow[];
  stages: StageRow[];
  stageMax: number;
  rows: GroupFolderRow[];
  regions: GroupRegionRow[];
  products: GroupProductRow[];
  productMax: number;
  productAmount: number;
  shippingFee: number;
  lastClick: Array<{ name: string; label: string; amount: number }>;
  lastClickMax: number;
  institutions: InstitutionSalesRow[];
  institutionTotals: InstitutionSalesTotals | null;
  institutionMax: number;
  timeline: Array<{
    name: string;
    sentAt: string;
    orders: number;
    amount: number;
    days: Array<{ day: string; orders: number; amount: number; note: string }>;
  }>;
};

const groupReport = ref<ReportView | null>(null);
const singleView = ref<ReportView | null>(null);

function activeReport(): ReportView | null {
  return groupReport.value;
}

type RegionSortKey = 'name' | 'sentOk' | 'opened' | 'clicked' | 'amount' | 'openRate' | 'clickRate';
const regionView = ref<'count' | 'rate'>('rate');
const regionSortKey = ref<RegionSortKey>('openRate');
const regionSortDesc = ref(true);
const productShowAll = ref(false);
const institutionShowAll = ref(false);
const tooltip = ref<{ show: boolean; x: number; y: number; lines: string[] }>({
  show: false,
  x: 0,
  y: 0,
  lines: [],
});

const KOREA_REGIONS = [
  { name: '경기', d: 'M 85 70 L 175 45 L 180 85 L 200 115 L 190 160 L 150 175 L 100 160 L 60 125 Z', lx: 132, ly: 112 },
  { name: '강원', d: 'M 175 45 L 245 28 L 305 55 L 340 105 L 330 165 L 300 200 L 255 195 L 225 155 L 200 115 L 180 85 Z', lx: 262, ly: 106 },
  { name: '충북', d: 'M 190 160 L 200 115 L 225 155 L 255 195 L 245 245 L 200 260 L 145 255 L 172 220 Z', lx: 210, ly: 186 },
  { name: '충남', d: 'M 100 160 L 150 175 L 190 160 L 172 220 L 145 255 L 95 245 L 55 195 Z', lx: 118, ly: 204 },
  { name: '전북', d: 'M 95 245 L 145 255 L 200 260 L 195 305 L 145 325 L 90 305 Z', lx: 142, ly: 286 },
  { name: '전남', d: 'M 90 305 L 145 325 L 195 305 L 210 340 L 175 380 L 105 375 L 55 345 Z', lx: 128, ly: 348 },
  { name: '경북', d: 'M 255 195 L 300 200 L 330 165 L 355 225 L 345 290 L 300 330 L 250 320 L 245 245 Z', lx: 300, ly: 252 },
  { name: '경남', d: 'M 245 245 L 250 320 L 300 330 L 290 370 L 240 380 L 210 340 L 195 305 L 200 260 Z', lx: 248, ly: 318 },
  { name: '제주', d: 'M 40 452 Q 40 412 110 412 Q 180 412 180 452 Q 180 492 110 492 Q 40 492 40 452 Z', lx: 110, ly: 450 },
];

const currentSlot = computed(
  () => slots.value.find((s) => s.slot === selectedSlot.value) || null
);

const formatNum = (n: number | undefined) => Number(n || 0).toLocaleString('ko-KR');

const formatSize = (n: number) => {
  const size = Number(n || 0);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value: string | Date | null | undefined) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatPct = (part: number, whole: number) =>
  whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : '0%';

const cleanProductName = (raw: string) => {
  const text = String(raw || '').replace(/\s+/g, ' ').trim();
  if (text.length < 4 || text.length > 40) return '';
  if (/비교|이유|서두르|거래처|일주일|^D-?\s*\d/.test(text)) return '';
  if (/[✓✔🌿♥❤]/.test(text)) return '';
  return text;
};

const describeMail = (folder: string) => {
  const title = promoMailLabel(folder)
    .replace(/\s*\d{14}\s*$/, '')
    .replace(/^\s*\(?\s*광고\s*\)?\s*/i, '')
    .replace(/[[\]（）()]/g, ' ')
    .replace(/[\u{1F000}-\u{1FAFF}\u2190-\u27BF\u2764\uFE0F]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (/best|베스트/i.test(title)) {
    return { short: 'BEST 5', angle: '상품을 순위로 소개', title };
  }
  if (/알뜰|실속/.test(title)) {
    return { short: '알뜰하게', angle: '가격·실속을 강조', title };
  }
  if (/못\s*고르|원장님/.test(title)) {
    return { short: '아직 못 고르셨나요', angle: '명절 직전 리마인드', title };
  }
  const short = title.length > 16 ? `${title.slice(0, 16)}…` : title || '메일';
  return { short, angle: '안내', title: title || short };
};

const koMonthDay = (value: string) => {
  const matched = String(value || '').match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!matched) return '';
  return `${Number(matched[2])}월 ${Number(matched[3])}일`;
};

const shortLabel = (folder: string) => describeMail(folder).short;

/* ── 종합 보고서: 추이 차트 ── */
const TREND_PAD_T = 14;
const TREND_PAD_B = 36;

const trend = computed(() => {
  const rows = activeReport()?.rows || [];
  const width = Math.max(560, rows.length * 110);
  const padL = 44;
  const padR = 18;
  const maxRate = Math.max(10, ...rows.flatMap((row) => [row.openRate, row.clickRate]));
  const yMax = Math.max(10, Math.ceil((maxRate * 1.15) / 5) * 5);
  const ticks = [0.25, 0.5, 0.75, 1].map((ratio) => Math.round(yMax * ratio * 10) / 10);
  const x = (i: number) =>
    rows.length > 1
      ? padL + (i * (width - padL - padR)) / (rows.length - 1)
      : padL + (width - padL - padR) / 2;
  const y = (v: number) => TREND_PAD_T + (1 - v / yMax) * (240 - TREND_PAD_T - TREND_PAD_B);
  return {
    width,
    padL,
    padR,
    yMax,
    ticks,
    openPoints: rows.map((row, i) => `${x(i)},${y(row.openRate)}`).join(' '),
    clickPoints: rows.map((row, i) => `${x(i)},${y(row.clickRate)}`).join(' '),
  };
});

const trendX = (index: number) => {
  const t = trend.value;
  const rows = activeReport()?.rows || [];
  return rows.length > 1
    ? t.padL + (index * (t.width - t.padL - t.padR)) / (rows.length - 1)
    : t.padL + (t.width - t.padL - t.padR) / 2;
};

const trendY = (value: number) =>
  TREND_PAD_T + (1 - value / trend.value.yMax) * (240 - TREND_PAD_T - TREND_PAD_B);

/* ── 종합 보고서: 지역 지도 ── */
const regionRow = (name: string) =>
  activeReport()?.regions.find((row) => row.name === name) || null;

const regionAmountMax = computed(() =>
  Math.max(
    0,
    ...(activeReport()?.regions || [])
      .filter((row) => row.name !== '미확인')
      .map((row) => row.amount || 0)
  )
);

const regionRatio = (name: string) => {
  const max = regionAmountMax.value;
  const row = regionRow(name);
  return max > 0 && row ? (row.amount || 0) / max : 0;
};

const regionRateText = (name: string, kind: 'open' | 'click') => {
  const row = regionRow(name);
  if (!row || row.sentOk <= 0) return '0%';
  const rate = kind === 'open' ? (row.opened / row.sentOk) * 100 : (row.clicked / row.sentOk) * 100;
  return `${rate.toFixed(1)}%`;
};

const regionMoneyText = (name: string) => `${formatNum(regionRow(name)?.amount || 0)}원`;

const regionFill = (name: string) => {
  const ratio = regionRatio(name);
  const from = [238, 244, 251];
  const to = [20, 80, 168];
  const mix = from.map((channel, i) => Math.round(channel + (to[i] - channel) * ratio));
  return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`;
};

const regionTextColor = (name: string) => (regionRatio(name) > 0.55 ? '#ffffff' : '#1e3a5f');

const regionOpenRate = (row: GroupRegionRow) => (row.sentOk > 0 ? (row.opened / row.sentOk) * 100 : 0);
const regionClickRate = (row: GroupRegionRow) => (row.sentOk > 0 ? (row.clicked / row.sentOk) * 100 : 0);
const regionSortValue = (row: GroupRegionRow, key: RegionSortKey) => {
  if (key === 'name') return row.name;
  if (key === 'openRate') return regionOpenRate(row);
  if (key === 'clickRate') return regionClickRate(row);
  return row[key] || 0;
};

const regionTotals = computed(() => {
  const rows = activeReport()?.regions || [];
  const sentOk = rows.reduce((sum, row) => sum + row.sentOk, 0);
  const opened = rows.reduce((sum, row) => sum + row.opened, 0);
  const clicked = rows.reduce((sum, row) => sum + row.clicked, 0);
  const amount = rows.reduce((sum, row) => sum + row.amount, 0);
  return {
    sentOk,
    opened,
    clicked,
    amount,
    openRate: sentOk > 0 ? (opened / sentOk) * 100 : 0,
    clickRate: sentOk > 0 ? (clicked / sentOk) * 100 : 0,
  };
});

const regionRows = computed(() => {
  const list = [...(activeReport()?.regions || [])];
  const dir = regionSortDesc.value ? -1 : 1;
  return list.sort((a, b) => {
    const left = regionSortValue(a, regionSortKey.value);
    const right = regionSortValue(b, regionSortKey.value);
    if (typeof left === 'string' && typeof right === 'string') return left.localeCompare(right, 'ko') * dir;
    return (Number(left) - Number(right)) * dir;
  });
});

const sortRegion = (key: RegionSortKey) => {
  if (regionSortKey.value === key) {
    regionSortDesc.value = !regionSortDesc.value;
    return;
  }
  regionSortKey.value = key;
  regionSortDesc.value = key !== 'name';
};

const setRegionView = (view: 'count' | 'rate') => {
  regionView.value = view;
  const keys: RegionSortKey[] = view === 'count'
    ? ['name', 'sentOk', 'opened', 'clicked', 'amount']
    : ['name', 'openRate', 'clickRate', 'amount'];
  if (!keys.includes(regionSortKey.value)) {
    regionSortKey.value = view === 'count' ? 'amount' : 'openRate';
    regionSortDesc.value = true;
  }
};

/* ── 종합 보고서: 상품 ── */
const productTop = computed(() => (activeReport()?.products || []).slice(0, 10));

const productTableRows = computed(() => {
  const list = activeReport()?.products || [];
  return productShowAll.value ? list : list.slice(0, 10);
});

const productTotals = computed(() =>
  (activeReport()?.products || []).reduce(
    (acc, row) => ({
      orders: acc.orders + row.orders,
      qty: acc.qty + row.qty,
      amount: acc.amount + row.amount,
    }),
    { orders: 0, qty: 0, amount: 0 }
  )
);

/* ── 종합 보고서: 기관별 매출·수수료 ── */
const institutionTop = computed(() => (activeReport()?.institutions || []).slice(0, 10));

const institutionTableRows = computed(() => {
  const list = activeReport()?.institutions || [];
  return institutionShowAll.value ? list : list.slice(0, 10);
});

const shown = computed(() => activeReport());

const clickEntryRate = (row: GroupFolderRow) =>
  row.clicks > 0 ? (row.clickBuyers / row.clicks) * 100 : 0;

const perThousand = (row: GroupFolderRow) =>
  row.values[0] > 0 ? (row.afterSendAmount / row.values[0]) * 1000 : 0;

const meeting = computed(() => {
  const view = shown.value;
  if (!view || !view.rows.length) return null;
  const mails = [...view.rows].sort((a, b) => a.sentAt.localeCompare(b.sentAt));
  const byMoney = [...view.rows].sort((a, b) => b.afterSendAmount - a.afterSendAmount);
  const byOpen = [...view.rows].sort((a, b) => b.openRate - a.openRate);
  const winner = byMoney[0];
  const openBest = byOpen[0];
  const openWorst = byOpen[byOpen.length - 1];
  const second = byMoney[1];

  const openMax = Math.max(...view.rows.map((row) => row.openRate), 1);
  const moneyMax = Math.max(...view.rows.map((row) => perThousand(row)), 1);

  let opener = `추석에 ${formatNum(view.count)}통을 보냈습니다.`;
  if (view.rows.length >= 2 && winner.afterSendAmount > 0) {
    opener = `「${winner.short}」가 가장 컸습니다. 오픈 ${winner.openRate.toFixed(1)}% · 구매 ${formatNum(winner.afterSendAmount)}원`;
  }

  const caveat =
    view.attributedOrders > 0 && view.attributedOrders < 40
      ? `구매 ${formatNum(view.attributedOrders)}건. 건수가 적어 순위는 참고입니다.`
      : '';

  const sentAvg =
    view.rows.reduce((sum, row) => sum + row.values[0], 0) / view.rows.length;
  let didLead = `발송 ${formatNum(view.sentOk)}건, ${koMonthDay(mails[0]?.sentAt) || ''}${
    mails.length > 1 ? `부터 ${koMonthDay(mails[mails.length - 1].sentAt)}까지` : ''
  } 진행했습니다.`;
  if (view.sameGoods && view.introduced.length) {
    didLead = `발송량은 메일마다 약 ${formatNum(Math.round(sentAvg))}건으로 비슷하고, 소개한 상품도 같습니다. 달라진 것은 제목의 방향과 발송일입니다.`;
  }

  const openInsight =
    view.rows.length >= 2
      ? `오픈율은 「${openBest.short}」 ${openBest.openRate.toFixed(1)}%와 「${openWorst.short}」 ${openWorst.openRate.toFixed(1)}%로 ${(openBest.openRate - openWorst.openRate).toFixed(1)}%p 차이입니다.`
      : '';

  let buyInsight = '';
  if (winner && winner.afterSendAmount > 0) {
    buyInsight = `발송 1,000건당 구매 금액은 「${winner.short}」 ${formatNum(Math.round(perThousand(winner)))}원입니다.`;
    if (second && second.values[0] > 0) {
      buyInsight += ` 「${second.short}」는 ${formatNum(Math.round(perThousand(second)))}원입니다.`;
    }
  }

  let afterClick = '';
  if (winner && second && winner.clicks >= 100 && second.clicks >= 100) {
    afterClick = `클릭 ${formatNum(winner.clicks)}건 중 구매 ${formatNum(winner.lastClickOrders)}건입니다. 「${second.short}」는 클릭 ${formatNum(second.clicks)}건 중 ${formatNum(second.lastClickOrders)}건입니다. 클릭한 사람의 구매 비율은 비슷하고, 「${winner.short}」가 클릭을 더 많이 만들었습니다.`;
  }

  const featured = view.products.filter((row) => row.isMail);
  const extra = view.products.filter((row) => !row.isMail);
  const productTotal = view.productMailAmount + view.productOtherAmount;
  const extraShare = productTotal > 0 ? (view.productOtherAmount / productTotal) * 100 : 0;
  let productInsight = '';
  if (featured[0] && extra[0] && extraShare >= 30) {
    productInsight = `소개 상품 중 판매액이 가장 큰 것은 「${featured[0].name}」입니다. 소개하지 않은 상품 가운데 가장 큰 것은 「${extra[0].name}」이고, 구매액의 ${extraShare.toFixed(0)}%가 소개하지 않은 상품입니다. 메일은 소개 상품 판매와 함께, 몰에서 다른 상품을 함께 사게 하는 역할도 했습니다.`;
  } else if (featured[0]) {
    productInsight = `소개 상품 중 판매액이 가장 큰 것은 「${featured[0].name}」 ${formatNum(featured[0].amount)}원입니다.`;
  }

  const timeline = (view.timeline || []).map((span) => ({
    ...span,
    short: describeMail(span.name).short,
  }));
  const dayMax = Math.max(1, ...timeline.flatMap((span) => span.days.map((day) => day.amount)));
  const spanMax = Math.max(0, ...timeline.map((span) => span.amount));

  return {
    opener,
    caveat,
    didLead,
    mails,
    timeline,
    dayMax,
    spanMax,
    winnerFolder: winner?.folder || '',
    openInsight,
    buyInsight,
    afterClick,
    productInsight,
    openMax,
    moneyMax,
    featured: featured.slice(0, 5),
    extra: extra.slice(0, 5),
  };
});

type ReportBrief = { kicker: string; stat: string; title: string; body?: string; tone?: 'warn' };

const reportBrief = computed((): ReportBrief[] => {
  const view = shown.value;
  if (!view) return [];
  const items: ReportBrief[] = [];

  if (view.rows.length >= 2) {
    const byOpen = [...view.rows].sort((a, b) => b.openRate - a.openRate);
    const best = byOpen[0];
    const worst = byOpen[byOpen.length - 1];
    const moneyBest = [...view.rows].sort((a, b) => b.lastClickAmount - a.lastClickAmount)[0];
    const gap = best.openRate - worst.openRate;
    const sameWinner = !moneyBest || moneyBest.lastClickAmount <= 0 || moneyBest.folder === best.folder;
    items.push({
      kicker: '메일 비교',
      stat: `${gap.toFixed(1)}%p`,
      title: sameWinner ? `오픈 1위 ${best.label}` : `오픈 ${best.label} · 매출 ${moneyBest.label}`,
      body: sameWinner ? '오픈율과 귀속 매출 모두 1위' : '오픈율 1위와 귀속 매출 1위가 다름',
    });
  } else if (view.sentOk > 0) {
    const openRate = (view.opened / view.sentOk) * 100;
    const clickOfOpen = view.opened > 0 ? (view.clicked / view.opened) * 100 : 0;
    const weakOpen = openRate < 10;
    const weakClick = !weakOpen && clickOfOpen < 12;
    items.push({
      kicker: '반응',
      stat: `${openRate.toFixed(1)}%`,
      title: '오픈율',
      body: weakOpen
        ? '열람이 낮음 — 제목·발송 시점 점검'
        : weakClick
          ? '클릭이 낮음 — 본문·버튼 점검'
          : '오픈 이후 클릭까지 안정적으로 이어짐',
    });
  }

  const regions = view.regions.filter((row) => row.name !== '미확인' && row.sentOk > 0);
  if (regions.length) {
    const bySent = [...regions].sort((a, b) => b.sentOk - a.sentOk)[0];
    const withMoney = regions.filter((row) => row.amount > 0);
    const byAmount = [...withMoney].sort((a, b) => b.amount - a.amount)[0];
    if (byAmount && bySent.name !== byAmount.name) {
      items.push({
        kicker: '지역',
        stat: `${bySent.name}→${byAmount.name}`,
        title: '발송 1위 · 구매 1위',
        body: '발송이 많은 지역과 구매가 많은 지역이 다름',
      });
    } else if (byAmount) {
      items.push({
        kicker: '지역',
        stat: byAmount.name,
        title: '발송·구매 모두 1위',
      });
    }
  }

  const unknown = view.institutions.find((row) => /미상/.test(row.name));
  const sales = view.institutionTotals?.sales || 0;
  const unknownShare = unknown && sales > 0 ? unknown.sales / sales : 0;
  if (unknown && unknownShare >= 0.15) {
    items.push({
      kicker: '확인 필요',
      stat: formatPct(unknown.sales, sales),
      title: '기관명 미확인 매출',
      tone: 'warn',
      body: '기관 순위는 보조 지표로만 해석',
    });
  } else if (view.products.length) {
    const mailAmount = view.products.filter((row) => row.isMail).reduce((sum, row) => sum + row.amount, 0);
    const otherAmount = view.products.filter((row) => !row.isMail).reduce((sum, row) => sum + row.amount, 0);
    const total = mailAmount + otherAmount;
    if (total > 0) {
      const otherShare = (otherAmount / total) * 100;
      items.push({
        kicker: '상품',
        stat: `${otherShare.toFixed(0)}%`,
        title: '추가 구매(미게재) 비중',
        body: '메일 클릭 후 몰에서 다른 상품을 함께 구매',
      });
    }
  }

  return items.slice(0, 3);
});

const reportNav = computed(() => {
  const view = shown.value;
  if (!view) return [];
  const items = [{ id: 'pml-sec-response', label: '전체 성과' }];
  if (view.rows.length > 1) items.push({ id: 'pml-sec-trend', label: '메일 비교' });
  if (view.stages.length) items.push({ id: 'pml-sec-stage', label: '구매 단계' });
  if (view.products.length) items.push({ id: 'pml-sec-product', label: '상품' });
  if (view.isGroup) items.push({ id: 'pml-sec-mail', label: '메일 기여' });
  items.push({ id: 'pml-sec-summary', label: '결론' });
  items.push({ id: 'pml-sec-region', label: '부록' });
  return items;
});

const confidence = computed(() => {
  const view = shown.value;
  if (!view) return { tone: 'is-mid', icon: 'mdi-information-outline', title: '', body: '' };
  const orders = view.attributedOrders;
  if (orders < 40) {
    return {
      tone: 'is-low',
      icon: 'mdi-alert-outline',
      title: '구매 건수가 적어 메일별 구매 순위는 참고 수치입니다',
      body: `발송·오픈·클릭은 ${formatNum(view.sentOk)}건 규모로 안정적이지만, 메일에 귀속된 구매는 ${formatNum(orders)}건입니다.`,
    };
  }
  return {
    tone: 'is-mid',
    icon: 'mdi-information-outline',
    title: '발송량이 다른 메일은 건수와 함께 비교하세요',
    body: `메일 귀속 구매 ${formatNum(orders)}건 기준입니다.`,
  };
});

type ConcLine = { head: string; tail: string };

const reportFindings = computed((): ConcLine[] => {
  const view = shown.value;
  if (!view) return [];
  const out: ConcLine[] = [];
  const openRate = view.sentOk > 0 ? (view.opened / view.sentOk) * 100 : 0;
  const clickRate = view.sentOk > 0 ? (view.clicked / view.sentOk) * 100 : 0;
  out.push({
    head: `오픈율 ${openRate.toFixed(1)}% · 클릭율 ${clickRate.toFixed(1)}%`,
    tail: ` — 발송 ${formatNum(view.sentOk)}건 기준`,
  });
  if (view.rows.length > 1) {
    const byOpen = [...view.rows].sort((a, b) => b.openRate - a.openRate);
    const best = byOpen[0];
    const worst = byOpen[byOpen.length - 1];
    out.push({
      head: `메일별 오픈율 격차 ${(best.openRate - worst.openRate).toFixed(1)}%p`,
      tail: ` — ${best.label} ${best.openRate.toFixed(1)}% / ${worst.label} ${worst.openRate.toFixed(1)}%`,
    });
    const moneyBest = [...view.rows].sort((a, b) => b.lastClickAmount - a.lastClickAmount)[0];
    if (moneyBest && moneyBest.lastClickAmount > 0) {
      out.push({
        head: `귀속 매출 1위 ${moneyBest.label}`,
        tail: ` ${formatNum(moneyBest.lastClickAmount)}원 (중복 주문 제외)`,
      });
    }
  }
  const total = view.productMailAmount + view.productOtherAmount;
  if (total > 0) {
    const otherShare = (view.productOtherAmount / total) * 100;
    out.push({
      head: `구매액의 ${otherShare.toFixed(0)}%가 추가 구매(미게재)`,
      tail: ' — 메일 클릭 후 몰에서 다른 상품을 함께 구매',
    });
  }
  return out;
});

const reportLimits = computed((): ConcLine[] => {
  const view = shown.value;
  if (!view) return [];
  const out: ConcLine[] = [
    { head: '인과관계가 아닌 시간 순서 추정', tail: ' — 구매가 해당 메일 때문이라고 단정할 수 없음' },
    { head: `메일 귀속 구매 ${formatNum(view.attributedOrders)}건`, tail: ' — 소수 주문으로 순위가 바뀔 수 있음' },
    { head: '지역은 광역 단위로 병합', tail: ' — 예: 서울·인천 → 경기' },
  ];
  const unknown = view.institutions.find((row) => /미상/.test(row.name));
  const sales = view.institutionTotals?.sales || 0;
  if (unknown && sales > 0 && unknown.sales / sales >= 0.15) {
    out.push({
      head: `기관명 미확인 ${formatPct(unknown.sales, sales)}`,
      tail: ' — 기관 순위 신뢰도가 낮음',
    });
  }
  return out;
});

const activeSection = ref('pml-sec-response');
let sectionObserver: IntersectionObserver | null = null;

const bindSectionObserver = () => {
  sectionObserver?.disconnect();
  const root = document.querySelector('.pml-group-report');
  if (!root) return;
  sectionObserver = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      const id = visible?.target?.id;
      if (id) activeSection.value = id;
    },
    { rootMargin: '-30% 0px -55% 0px', threshold: [0.1, 0.25, 0.5] }
  );
  root.querySelectorAll('[data-report-sec]').forEach((el) => sectionObserver?.observe(el));
};

const scrollToSection = (id: string) => {
  activeSection.value = id;
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

watch(shown, async (view) => {
  if (!view) {
    sectionObserver?.disconnect();
    return;
  }
  activeSection.value = 'pml-sec-response';
  await nextTick();
  bindSectionObserver();
});

const stageClass = (index: number) => ['is-mail', 'is-stage-open', 'is-stage-skip'][index] || 'is-other';

const stageTipLines = (step: StageRow) => [
  step.label,
  `구매 기관 ${formatNum(step.buyers)}곳 · 주문 ${formatNum(step.orders)}건`,
  `금액 ${formatNum(step.amount)}원`,
];

const institutionTipLines = (row: InstitutionSalesRow) => [
  row.name,
  row.viaClick ? '클릭 후 구매' : '클릭 없이 바로 구매',
  `주문 ${formatNum(row.orders)}건 · 상품판매금액 ${formatNum(row.sales)}원`,
  `수수료 ${formatNum(row.commission)}원 (정산 완료 ${formatNum(row.settled)}원 · 대기 ${formatNum(row.pending)}원)`,
];

/* ── 공용 툴팁 (지연 없이 즉시 표시) ── */
const showTip = (event: MouseEvent, lines: string[]) => {
  tooltip.value = { show: true, x: event.clientX + 14, y: event.clientY + 14, lines };
};

const moveTip = (event: MouseEvent) => {
  if (!tooltip.value.show) return;
  tooltip.value = { ...tooltip.value, x: event.clientX + 14, y: event.clientY + 14 };
};

const hideTip = () => {
  tooltip.value = { ...tooltip.value, show: false };
};

const trendTipLines = (row: GroupFolderRow) => [
  row.label,
  `오픈율 ${row.openRate.toFixed(1)}% · 클릭율 ${row.clickRate.toFixed(1)}%`,
  `발송 성공 ${formatNum(row.values[0])}건`,
];

const regionTipLines = (name: string) => {
  const row = regionRow(name);
  if (!row) return [name, '데이터 없음'];
  return [
    name,
    `발송 성공 ${formatNum(row.sentOk)}건`,
    `오픈 ${formatNum(row.opened)}건 · 클릭 ${formatNum(row.clicked)}건`,
    `구매 ${formatNum(row.orders)}건 · ${formatNum(row.amount)}원`,
  ];
};

const productTipLines = (product: GroupProductRow) => [
  product.name,
  product.isMail ? '메일 게재 상품' : '추가 구매(미게재)',
  `주문 ${formatNum(product.orders)}건 · 수량 ${formatNum(product.qty)}개`,
  `판매금액 ${formatNum(product.amount)}원`,
];

const selectedReport = computed(
  () => statsReports.value.find((item) => item.folder === selectedFolder.value) || null
);

const looseItems = computed(() => statsReports.value.filter((item) => !item.groupIdx));

const groupItems = (idx: number) => statsReports.value.filter((item) => item.groupIdx === idx);

const isGroupOpen = (idx: number) => openGroupIds.value.includes(idx);

const toggleGroupOpen = (idx: number) => {
  openGroupIds.value = isGroupOpen(idx)
    ? openGroupIds.value.filter((id) => id !== idx)
    : [...openGroupIds.value, idx];
};

const groupAllChecked = (idx: number) => {
  const names = groupItems(idx).map((item) => item.folder);
  return names.length > 0 && names.every((name) => checkedFolders.value.includes(name));
};

const toggleGroupCheck = (idx: number) => {
  const names = groupItems(idx).map((item) => item.folder);
  if (!names.length) return;
  if (groupAllChecked(idx)) {
    checkedFolders.value = checkedFolders.value.filter((name) => !names.includes(name));
    return;
  }
  checkedFolders.value = [...new Set([...checkedFolders.value, ...names])];
};

const rememberGroups = (groups: Array<{ idx: number; name: string }> | undefined) => {
  const list = Array.isArray(groups) ? groups : [];
  userGroups.value = list;
  const known = new Set(openGroupIds.value);
  list.forEach((group) => {
    if (!known.has(group.idx)) openGroupIds.value = [...openGroupIds.value, group.idx];
  });
  openGroupIds.value = openGroupIds.value.filter((id) => list.some((group) => group.idx === id));
};

const createUserGroup = async () => {
  const name = newGroupName.value.trim();
  if (!name) return;
  busy.value = true;
  busyText.value = '폴더를 만드는 중...';
  errorMessage.value = '';
  try {
    const res = await promoMailListApi.createGroup(name);
    rememberGroups(res.data?.data?.groups);
    newGroupName.value = '';
  } catch (error: any) {
    errorMessage.value = error.response?.data?.message || error.message || '폴더를 만들지 못했습니다.';
  } finally {
    busy.value = false;
  }
};

const startRename = (group: { idx: number; name: string }) => {
  editingGroup.value = group.idx;
  editingName.value = group.name;
};

const saveGroupName = async (idx: number) => {
  const name = editingName.value.trim();
  if (!name) return;
  busy.value = true;
  busyText.value = '폴더 이름을 바꾸는 중...';
  errorMessage.value = '';
  try {
    const res = await promoMailListApi.renameGroup(idx, name);
    rememberGroups(res.data?.data?.groups);
    editingGroup.value = 0;
  } catch (error: any) {
    errorMessage.value = error.response?.data?.message || error.message || '폴더 이름을 바꾸지 못했습니다.';
  } finally {
    busy.value = false;
  }
};

const removeUserGroup = async (group: { idx: number; name: string }) => {
  if (!window.confirm(`「${group.name}」 폴더만 지웁니다. 안의 통계는 폴더 밖으로 나옵니다.`)) return;
  busy.value = true;
  busyText.value = '폴더를 지우는 중...';
  errorMessage.value = '';
  try {
    const res = await promoMailListApi.deleteGroup(group.idx);
    statsReports.value = statsReports.value.map((item) => (
      item.groupIdx === group.idx ? { ...item, groupIdx: null } : item
    ));
    rememberGroups(res.data?.data?.groups);
    if (editingGroup.value === group.idx) editingGroup.value = 0;
  } catch (error: any) {
    errorMessage.value = error.response?.data?.message || error.message || '폴더를 지우지 못했습니다.';
  } finally {
    busy.value = false;
  }
};

const clearDropOpenTimer = () => {
  if (dropOpenTimer) {
    clearTimeout(dropOpenTimer);
    dropOpenTimer = null;
  }
};

const onItemDragStart = (event: DragEvent, folder: string) => {
  const target = event.target as HTMLElement | null;
  if (target && target.closest('input, .pml-mini-btn, .pml-out-btn')) {
    event.preventDefault();
    return;
  }
  const names = checkedFolders.value.includes(folder)
    ? [...checkedFolders.value]
    : [folder];
  dragNames.value = names;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', names.join('\n'));
  }
};

const onItemDragEnd = () => {
  dragNames.value = [];
  dropHover.value = null;
  clearDropOpenTimer();
};

const draggedAlreadyIn = (idx: number | null) => dragNames.value.length > 0 && dragNames.value.every((name) => {
  const item = statsReports.value.find((row) => row.folder === name);
  return item && item.groupIdx === idx;
});

const draggingGrouped = computed(() => dragNames.value.some((name) => {
  const item = statsReports.value.find((row) => row.folder === name);
  return !!item && item.groupIdx != null;
}));

const onDragOverFolder = (event: DragEvent, idx: number) => {
  if (!dragNames.value.length || draggedAlreadyIn(idx)) {
    if (dropHover.value === 'root') dropHover.value = null;
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  if (dropHover.value !== idx) {
    dropHover.value = idx;
    clearDropOpenTimer();
    if (!isGroupOpen(idx)) {
      dropOpenTimer = setTimeout(() => {
        if (dropHover.value === idx && !isGroupOpen(idx)) toggleGroupOpen(idx);
      }, 450);
    }
  }
};

const onDragLeaveFolder = (event: DragEvent, idx: number) => {
  const next = event.relatedTarget as Node | null;
  const current = event.currentTarget as HTMLElement | null;
  if (current && next && current.contains(next)) return;
  if (dropHover.value === idx) dropHover.value = null;
};

const onDragOverOut = (event: DragEvent) => {
  if (!dragNames.value.length || draggedAlreadyIn(null)) return;
  const target = event.target as HTMLElement | null;
  if (target && target.closest('.pml-user-folder')) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  dropHover.value = 'root';
  clearDropOpenTimer();
};

const onDragLeaveOut = (event: DragEvent) => {
  const next = event.relatedTarget as Node | null;
  const current = event.currentTarget as HTMLElement | null;
  if (current && next && current.contains(next)) return;
  if (dropHover.value === 'root') dropHover.value = null;
};

const takeOut = (folder: string) => {
  const names = checkedFolders.value.includes(folder)
    ? checkedFolders.value.filter((name) => {
      const item = statsReports.value.find((row) => row.folder === name);
      return !!item && item.groupIdx != null;
    })
    : [folder];
  moveItems(names, null);
};

const moveItems = async (names: string[], groupIdx: number | null) => {
  const moving = names.filter((name) => {
    const item = statsReports.value.find((row) => row.folder === name);
    return item && item.groupIdx !== groupIdx;
  });
  if (!moving.length) return;
  busy.value = true;
  busyText.value = groupIdx == null ? '폴더 밖으로 빼는 중...' : '폴더로 옮기는 중...';
  errorMessage.value = '';
  try {
    await promoMailListApi.moveCampaigns(moving, groupIdx);
    statsReports.value = statsReports.value.map((item) => (
      moving.includes(item.folder) ? { ...item, groupIdx } : item
    ));
  } catch (error: any) {
    errorMessage.value = error.response?.data?.message || error.message || '폴더를 옮기지 못했습니다.';
  } finally {
    busy.value = false;
  }
};

const onDropFolder = (idx: number) => {
  const names = [...dragNames.value];
  dropHover.value = null;
  clearDropOpenTimer();
  dragNames.value = [];
  moveItems(names, idx);
};

const onDropRoot = () => {
  const names = [...dragNames.value];
  dropHover.value = null;
  clearDropOpenTimer();
  dragNames.value = [];
  moveItems(names, null);
};

const allChecked = computed(
  () => statsReports.value.length > 0 && checkedFolders.value.length === statsReports.value.length
);
const someChecked = computed(() => checkedFolders.value.length > 0);

const selectFolder = (folder: string) => {
  selectedFolder.value = folder;
  htmlOpen.value = false;
};

const toggleCheckAll = () => {
  checkedFolders.value = allChecked.value ? [] : statsReports.value.map((item) => item.folder);
};

const deleteChecked = async () => {
  if (!checkedFolders.value.length) return;
  const names = [...checkedFolders.value];
  if (!window.confirm(`선택한 ${names.length}개 폴더의 저장된 통계를 삭제할까요?`)) return;
  busy.value = true;
  busyText.value = '선택한 통계를 삭제하는 중...';
  errorMessage.value = '';
  try {
    await promoMailListApi.deleteCampaigns(names);
    statsReports.value = statsReports.value.filter((item) => !names.includes(item.folder));
    checkedFolders.value = [];
    groupReport.value = null;
    if (names.includes(selectedFolder.value)) {
      selectedFolder.value = '';
      htmlOpen.value = false;
    }
  } catch (error: any) {
    errorMessage.value =
      error.response?.data?.message || error.message || '통계 삭제에 실패했습니다.';
  } finally {
    busy.value = false;
  }
};

const rowKey = (item: CaionEmailItem, idx: number) =>
  `${item.email || 'none'}::${item.name}::${idx}`;

const filteredCaionItems = computed(() => {
  const q = caionSearch.value.trim().toLowerCase();
  if (!q) return caionItems.value;
  return caionItems.value.filter((item) => {
    const name = item.name.toLowerCase();
    const email = (item.email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  });
});

const copyableEmails = computed(() => {
  const seen = new Set<string>();
  const list: string[] = [];
  for (const item of filteredCaionItems.value) {
    if (!item.hasEmail || !item.email) continue;
    const email = item.email.trim().toLowerCase();
    if (seen.has(email)) continue;
    seen.add(email);
    list.push(item.email.trim());
  }
  return list;
});

const loadStats = async () => {
  busy.value = true;
  busyText.value = '집계 중...';
  errorMessage.value = '';
  try {
    const res = await promoMailListApi.stats();
    stats.value = res.data?.data?.stats || stats.value;
    mailLoaded.value = true;
  } catch (error: any) {
    errorMessage.value =
      error.response?.data?.message || error.message || '집계에 실패했습니다.';
  } finally {
    busy.value = false;
  }
};

const blobErrorMessage = async (error: any, fallback: string) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return error?.response?.data?.message || error?.message || fallback;
};

const downloadExcel = async () => {
  if (stats.value.uniqueEmails < 1) return;
  busy.value = true;
  busyText.value = '엑셀을 만드는 중...';
  errorMessage.value = '';
  try {
    await promoMailListApi.downloadExcel();
  } catch (error: any) {
    errorMessage.value = await blobErrorMessage(error, '엑셀을 받지 못했습니다.');
  } finally {
    busy.value = false;
  }
};

const loadCaionEmails = async () => {
  caionLoading.value = true;
  caionError.value = '';
  try {
    const res = await promoMailListApi.caionEmails();
    const data = res.data?.data;
    caionItems.value = data?.items || [];
    caionWithEmail.value = data?.withEmail || 0;
    caionWithoutEmail.value = data?.withoutEmail || 0;
  } catch (error: any) {
    caionError.value =
      error.response?.data?.message || error.message || '목록을 불러오지 못했습니다.';
    caionItems.value = [];
  } finally {
    caionLoading.value = false;
  }
};

const openCaionModal = async () => {
  caionModalOpen.value = true;
  caionSearch.value = '';
  copiedKey.value = '';
  await loadCaionEmails();
};

const markCopied = (key: string) => {
  copiedKey.value = key;
  if (copiedTimer) clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => {
    if (copiedKey.value === key) copiedKey.value = '';
  }, 1500);
};

const copyEmail = async (item: CaionEmailItem, idx: number) => {
  if (!item.hasEmail || !item.email) return;
  try {
    await navigator.clipboard.writeText(item.email);
    markCopied(rowKey(item, idx));
  } catch {
    errorMessage.value = '클립보드 복사에 실패했습니다.';
  }
};

const copyAllEmails = async () => {
  if (copyableEmails.value.length < 1) return;
  try {
    await navigator.clipboard.writeText(copyableEmails.value.join('\n'));
    markCopied('all');
  } catch {
    errorMessage.value = '클립보드 복사에 실패했습니다.';
  }
};

const loadLandingSlots = async () => {
  busy.value = true;
  busyText.value = '랜딩 목록 불러오는 중...';
  errorMessage.value = '';
  try {
    const res = await promoHtmlApi.listSlots();
    slots.value = res.data?.data?.slots || [];
    if (!slots.value.find((s) => s.slot === selectedSlot.value) && slots.value[0]) {
      selectedSlot.value = slots.value[0].slot;
    }
    landingLoaded.value = true;
  } catch (error: any) {
    errorMessage.value =
      error.response?.data?.message || error.message || '랜딩 목록을 불러오지 못했습니다.';
  } finally {
    busy.value = false;
  }
};

const loadLandingStats = async () => {
  if (selectedSlot.value !== 'b') return;
  landingStatsError.value = '';
  try {
    const res = await promoHtmlApi.landingStats('b');
    landingStats.value = res.data?.data || null;
  } catch (error: any) {
    landingStats.value = null;
    landingStatsError.value =
      error.response?.data?.message || error.message || '집계를 불러오지 못했습니다.';
  }
};

const removeClicker = async (row: LandingClicker) => {
  if (!isSuperAdmin.value) {
    landingStatsError.value = '최고관리자만 삭제할 수 있습니다.';
    return;
  }
  const ok = window.confirm(
    `${row.who}의 클릭 ${formatNum(row.clickCount)}회를 삭제하면 클릭하여 들어온 횟수에서 빠집니다.`
  );
  if (!ok) return;
  deletingVisitor.value = row.visitor;
  landingStatsError.value = '';
  try {
    const res = await promoHtmlApi.deleteClicker('b', row.visitor);
    landingStats.value = res.data?.data?.stats || null;
  } catch (error: any) {
    landingStatsError.value =
      error.response?.data?.message || error.message || '클릭 기록을 지우지 못했습니다.';
  } finally {
    deletingVisitor.value = '';
  }
};

const copyApplyLink = async () => {
  try {
    await navigator.clipboard.writeText(APPLY_LINK);
    applyLinkCopied.value = true;
    if (applyLinkTimer) clearTimeout(applyLinkTimer);
    applyLinkTimer = setTimeout(() => {
      applyLinkCopied.value = false;
    }, 1600);
  } catch {
    errorMessage.value = '링크 복사에 실패했습니다.';
  }
};

const switchToLanding = async () => {
  mainTab.value = 'landing';
  if (!landingLoaded.value) await loadLandingSlots();
  if (selectedSlot.value === 'b') await loadLandingStats();
};

watch(selectedSlot, () => {
  if (mainTab.value === 'landing' && selectedSlot.value === 'b') loadLandingStats();
});

const switchToStats = async () => {
  mainTab.value = 'stats';
  if (!statsLoaded.value) await loadSavedStats();
};

const openStatsFolder = () => {
  statsFolderInputRef.value?.click();
};

type CampaignRow = {
  name: string;
  groupIdx?: number | null;
  htmlName?: string;
  html?: string;
  savedAt?: string;
  stats: StoredCampaignStats;
  region: RegionMatchResult;
  effect: CampaignEffectRow | null;
};

const showSavedAnalysis = (data: {
  campaigns?: CampaignRow[];
  groups?: Array<{ idx: number; name: string }>;
  lastClick: EffectMatchResult['lastClick'];
  boughtWithoutClick: EffectMatchResult['boughtWithoutClick'];
}) => {
  if (!data) return;
  const campaigns = data.campaigns || [];
  rememberGroups(data.groups);
  statsReports.value = campaigns.map((row) => ({
    folder: row.name,
    savedAt: row.savedAt || '',
    htmlFiles: row.html ? [{ name: row.htmlName || 'mail.html', html: row.html }] : [],
    groupIdx: row.groupIdx || null,
    stats: row.stats,
    region: row.region,
    effectRow: row.effect || null,
  }));
  if (selectedFolder.value && !campaigns.some((row) => row.name === selectedFolder.value)) {
    selectedFolder.value = '';
    htmlOpen.value = false;
  }
  checkedFolders.value = checkedFolders.value.filter((name) =>
    campaigns.some((row) => row.name === name)
  );
};

const buildGroupReport = (data: {
  campaigns?: CampaignRow[];
  lastClick: EffectMatchResult['lastClick'];
  boughtWithoutClick?: EffectMatchResult['boughtWithoutClick'];
  afterSend?: EffectMatchResult['afterSend'];
  afterSendRegions?: EffectMatchResult['afterSendRegions'];
  afterSendProducts?: EffectMatchResult['afterSendProducts'];
  afterSendShipping?: EffectMatchResult['afterSendShipping'];
  afterSendTimeline?: EffectMatchResult['afterSendTimeline'];
  institutions?: InstitutionSalesRow[];
  institutionTotals?: InstitutionSalesTotals | null;
}, target: 'group' | 'single' = 'group') => {
  const campaigns = data?.campaigns || [];
  if (!campaigns.length) {
    if (target === 'group') groupReport.value = null;
    else singleView.value = null;
    return;
  }
  const isGroup = campaigns.length > 1;

  const lastClick = (data.lastClick || []).map((row) => ({
    name: row.name,
    label: shortLabel(row.name),
    amount: Number(row.amount) || 0,
  }));
  const lastClickByName = new Map(
    (data.lastClick || []).map((row) => [
      row.name,
      { amount: Number(row.amount) || 0, orders: Number(row.orders) || 0 },
    ])
  );
  const afterSendByName = new Map(
    (data.afterSend || []).map((row) => [
      row.name,
      { amount: Number(row.amount) || 0, orders: Number(row.orders) || 0 },
    ])
  );
  const lastClickOrders = (data.lastClick || []).reduce(
    (total, row) => total + (Number(row.orders) || 0),
    0
  );

  const rows: GroupFolderRow[] = campaigns.map((row) => {
    const sentOk = Number(row.stats?.sentOk) || 0;
    const opened = Number(row.stats?.opened) || 0;
    const clicked = Number(row.stats?.clicked) || 0;
    const orders = Number(row.effect?.clicked?.orders) || 0;
    const profile = describeMail(row.name);
    const attributed = lastClickByName.get(row.name);
    const afterSend = afterSendByName.get(row.name);
    return {
      folder: row.name,
      label: profile.short,
      short: profile.short,
      angle: profile.angle,
      sentAt: row.stats?.sentRange?.from || '',
      values: [sentOk, opened, clicked, orders],
      openRate: sentOk > 0 ? (opened / sentOk) * 100 : 0,
      clickRate: sentOk > 0 ? (clicked / sentOk) * 100 : 0,
      clicks: clicked,
      clickBuyers: Number(row.effect?.clicked?.buyers) || 0,
      effectAmount: Number(row.effect?.clicked?.amount) || 0,
      lastClickAmount: attributed?.amount || 0,
      lastClickOrders: attributed?.orders || 0,
      afterSendAmount: afterSend?.amount || 0,
      afterSendOrders: afterSend?.orders || 0,
    };
  });

  const regionMap = new Map<string, GroupRegionRow>();
  const regionBucket = (name: string) => {
    if (!regionMap.has(name)) {
      regionMap.set(name, { name, sentOk: 0, opened: 0, clicked: 0, orders: 0, amount: 0 });
    }
    return regionMap.get(name)!;
  };
  campaigns.forEach((row) => {
    (row.region?.provinces || []).forEach((province) => {
      const bucket = regionBucket(province.name);
      bucket.sentOk += province.sentOk || 0;
      bucket.opened += province.opened || 0;
      bucket.clicked += province.clicked || 0;
    });
    (row.effect?.provinces || []).forEach((province) => {
      if (data.afterSendRegions) return;
      const bucket = regionBucket(province.name);
      bucket.orders += province.orders || 0;
      bucket.amount += province.amount || 0;
    });
  });
  const unknown: GroupRegionRow = {
    name: '미확인',
    sentOk: 0,
    opened: 0,
    clicked: 0,
    orders: 0,
    amount: 0,
  };
  campaigns.forEach((row) => {
    unknown.sentOk += row.region?.unmatched?.sentOk || 0;
    unknown.opened += row.region?.unmatched?.opened || 0;
    unknown.clicked += row.region?.unmatched?.clicked || 0;
    if (!data.afterSendRegions) {
      unknown.orders += row.effect?.unknownRegion?.orders || 0;
      unknown.amount += row.effect?.unknownRegion?.amount || 0;
    }
  });
  (data.afterSendRegions || []).forEach((province) => {
    const name = province.name === '지역 미상' ? '미확인' : province.name;
    const bucket = name === '미확인' ? unknown : regionBucket(name);
    bucket.orders += Number(province.orders) || 0;
    bucket.amount += Number(province.amount) || 0;
  });
  const regions = [...regionMap.values()];
  if (unknown.sentOk || unknown.opened || unknown.clicked || unknown.amount || unknown.orders) {
    regions.push(unknown);
  }

  const productMap = new Map<string, GroupProductRow>();
  const mergeProduct = (item: { name: string; qty: number; orders: number; amount: number }, isMail: boolean) => {
    if (!productMap.has(item.name)) {
      productMap.set(item.name, { name: item.name, isMail, qty: 0, orders: 0, amount: 0 });
    }
    const bucket = productMap.get(item.name)!;
    bucket.isMail = bucket.isMail || isMail;
    bucket.qty += Number(item.qty) || 0;
    bucket.orders += Number(item.orders) || 0;
    bucket.amount += Number(item.amount) || 0;
  };
  if (Array.isArray(data.afterSend)) {
    (data.afterSendProducts || []).forEach((item) => mergeProduct(item, Boolean(item.isMail)));
  } else {
    campaigns.forEach((row) => {
      (row.effect?.mailProducts || []).forEach((item) => mergeProduct(item, true));
      (row.effect?.otherProducts || []).forEach((item) => mergeProduct(item, false));
    });
  }
  const products = [...productMap.values()].sort((a, b) => b.amount - a.amount);

  const totals = campaigns.reduce(
    (acc, row) => ({
      sentOk: acc.sentOk + (Number(row.stats?.sentOk) || 0),
      sentFail: acc.sentFail + (Number(row.stats?.sentFail) || 0),
      opened: acc.opened + (Number(row.stats?.opened) || 0),
      clicked: acc.clicked + (Number(row.stats?.clicked) || 0),
      unsub: acc.unsub + (Number(row.stats?.unsub) || 0),
      buyers: acc.buyers + (Number(row.effect?.clicked?.buyers) || 0),
      orders: acc.orders + (Number(row.effect?.clicked?.orders) || 0),
      amount: acc.amount + (Number(row.effect?.clicked?.amount) || 0),
      cartOnly: acc.cartOnly + (Number(row.effect?.cartOnly) || 0),
    }),
    { sentOk: 0, sentFail: 0, opened: 0, clicked: 0, unsub: 0, buyers: 0, orders: 0, amount: 0, cartOnly: 0 }
  );
  const { sentOk, sentFail, opened, clicked, unsub, buyers, orders, amount, cartOnly } = totals;
  const boughtWithoutClick = data.boughtWithoutClick || { buyers: 0, orders: 0, amount: 0 };
  const institutions = (data.institutions || []).filter((row) => row.sales > 0 || row.orders > 0);
  const institutionTotals = data.institutionTotals || null;

  const failMap = new Map<string, number>();
  const linkMap = new Map<string, LinkRow>();
  let periodFrom = '';
  let periodTo = '';
  campaigns.forEach((row) => {
    (row.stats?.failReasons || []).forEach((reason) => {
      const name = String(reason.name || '').trim();
      if (!name) return;
      failMap.set(name, (failMap.get(name) || 0) + (Number(reason.count) || 0));
    });
    (row.stats?.links || []).forEach((link) => {
      const url = String(link.url || '').trim();
      if (!url) return;
      const prev = linkMap.get(url) || { url, clicks: 0, dup: 0 };
      prev.clicks += Number(link.clicks) || 0;
      prev.dup += Number(link.dup) || 0;
      linkMap.set(url, prev);
    });
    const from = row.stats?.sentRange?.from || '';
    const to = row.stats?.sentRange?.to || from;
    if (from && (!periodFrom || from < periodFrom)) periodFrom = from;
    if (to && (!periodTo || to > periodTo)) periodTo = to;
  });

  const stageSum = (key: 'clicked' | 'openOnly' | 'notOpen') =>
    campaigns.reduce(
      (acc, row) => {
        const item = row.effect?.[key];
        return {
          buyers: acc.buyers + (Number(item?.buyers) || 0),
          orders: acc.orders + (Number(item?.orders) || 0),
          amount: acc.amount + (Number(item?.amount) || 0),
        };
      },
      { buyers: 0, orders: 0, amount: 0 }
    );
  const stages: StageRow[] = [
    { label: '클릭 후 구매', ...stageSum('clicked') },
    { label: '오픈만 하고 구매', ...stageSum('openOnly') },
    { label: '미오픈 구매', ...stageSum('notOpen') },
  ].filter((step) => step.amount > 0 || step.orders > 0);

  const openRate = sentOk > 0 ? ((opened / sentOk) * 100).toFixed(1) : '0';
  const clickRate = sentOk > 0 ? ((clicked / sentOk) * 100).toFixed(1) : '0';
  const clickOfOpen = opened > 0 ? ((clicked / opened) * 100).toFixed(1) : '0';
  const clickOpenN = Number(clickOfOpen);
  let responseLead = '';
  if (Number(openRate) < 10) {
    responseLead = '오픈율이 낮습니다. 제목과 발송 시점을 점검하세요.';
  } else if (clickOpenN < 12) {
    responseLead = '오픈 대비 클릭이 낮습니다. 본문과 버튼을 점검하세요.';
  } else {
    responseLead = '오픈 이후 클릭까지 안정적으로 이어졌습니다.';
  }

  let trendLead = '';
  if (rows.length >= 2) {
    const byOpen = [...rows].sort((a, b) => b.openRate - a.openRate);
    const best = byOpen[0];
    const worst = byOpen[byOpen.length - 1];
    const gap = best.openRate - worst.openRate;
    const moneyBest = [...rows].sort((a, b) => b.lastClickAmount - a.lastClickAmount)[0];
    const sameWinner = !moneyBest || moneyBest.lastClickAmount <= 0 || moneyBest.folder === best.folder;
    trendLead = sameWinner
      ? `오픈율 1위 ${best.label}가 귀속 매출도 1위입니다. 최저 대비 ${gap.toFixed(1)}%p 높습니다.`
      : `오픈율 1위는 ${best.label}, 귀속 매출 1위는 ${moneyBest.label}입니다.`;
  }

  const namedRegions = regions.filter((row) => row.name !== '미확인' && row.sentOk > 0);
  const regionSent = [...namedRegions].sort((a, b) => b.sentOk - a.sentOk)[0];
  const regionBuy = [...namedRegions].filter((row) => row.amount > 0).sort((a, b) => b.amount - a.amount)[0];
  let regionLead = '';
  if (regionSent && regionBuy && regionSent.name !== regionBuy.name) {
    regionLead = `발송 1위는 ${regionSent.name}, 구매 1위는 ${regionBuy.name}입니다.`;
  } else if (regionBuy) {
    regionLead = `발송·구매 모두 ${regionBuy.name}이(가) 1위입니다.`;
  }

  const topStage = [...stages].sort((a, b) => b.amount - a.amount)[0];
  let stageLead = '';
  if (clicked > 0 && buyers > 0) {
    stageLead = `클릭 기관의 ${((buyers / clicked) * 100).toFixed(1)}%가 구매로 이어졌습니다.`;
  } else if (topStage) {
    stageLead = `구매 금액이 가장 큰 단계는 「${topStage.label}」입니다.`;
  }

  const mailProductAmount = products.filter((row) => row.isMail).reduce((total, row) => total + row.amount, 0);
  const otherProductAmount = products.filter((row) => !row.isMail).reduce((total, row) => total + row.amount, 0);
  const productAmountAll = mailProductAmount + otherProductAmount;
  let productLead = '';
  if (products.length && productAmountAll > 0) {
    const otherShare = (otherProductAmount / productAmountAll) * 100;
    productLead = otherShare >= 40
      ? `구매액의 ${otherShare.toFixed(0)}%는 메일에 소개하지 않은 상품(추가 구매)입니다.`
      : `판매 1위는 「${products[0].name}」입니다.`;
  }

  let institutionLead = '';
  if (institutions.length) {
    const totalSales = institutionTotals?.sales || 0;
    const unknownInst = institutions.find((row) => /미상/.test(row.name));
    const unknownHeavy = Boolean(unknownInst && totalSales > 0 && unknownInst.sales / totalSales >= 0.15);
    if (unknownHeavy && unknownInst) {
      institutionLead = `기관명 미확인 매출이 ${formatPct(unknownInst.sales, totalSales)}입니다. 순위는 보조로만 보세요.`;
    } else if (institutions[0]) {
      institutionLead = `구매 1위는 「${institutions[0].name}」입니다.`;
    }
  }

  const topLastClick = [...lastClick].sort((a, b) => b.amount - a.amount)[0];
  let mailLead = '';
  if (topLastClick && topLastClick.amount > 0) {
    mailLead = `중복 주문을 제외하면 「${topLastClick.label}」이(가) 귀속 매출 1위입니다.`;
  }

  const periodNote = periodFrom
    ? `발송 기간 ${periodFrom}${periodTo && periodTo !== periodFrom ? ` ~ ${periodTo}` : ''}`
    : '';

  const introduced: string[] = [];
  const goodsKeys: string[] = [];
  campaigns.forEach((row) => {
    const names = mailProductNames(row.html || '')
      .map((name) => cleanProductName(name))
      .filter(Boolean);
    goodsKeys.push(names.slice().sort().join('|'));
    names.forEach((name) => {
      if (!introduced.includes(name)) introduced.push(name);
    });
  });
  const sameGoods = goodsKeys.length > 1 && new Set(goodsKeys).size === 1;

  const view: ReportView = {
    isGroup,
    title: isGroup
      ? `선택한 ${campaigns.length}개 폴더 종합 보고서`
      : promoMailLabel(campaigns[0].name),
    periodNote,
    count: campaigns.length,
    sentOk,
    sentFail,
    opened,
    clicked,
    unsub,
    buyers,
    orders,
    amount,
    avgOrder: orders > 0 ? Math.round(amount / orders) : 0,
    cartOnly,
    boughtWithoutClick,
    attributedAmount: Array.isArray(data.afterSend)
      ? data.afterSend.reduce((total, row) => total + (Number(row.amount) || 0), 0)
      : lastClick.reduce((total, row) => total + row.amount, 0) + boughtWithoutClick.amount,
    attributedOrders: Array.isArray(data.afterSend)
      ? data.afterSend.reduce((total, row) => total + (Number(row.orders) || 0), 0)
      : lastClickOrders + boughtWithoutClick.orders,
    productMailAmount: mailProductAmount,
    productOtherAmount: otherProductAmount,
    introduced,
    sameGoods,
    leads: {
      response: responseLead,
      trend: trendLead,
      region: regionLead,
      stage: stageLead,
      product: productLead,
      institution: institutionLead,
      mail: mailLead,
    },
    failReasons: [...failMap.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8),
    links: [...linkMap.values()].sort((a, b) => b.clicks - a.clicks).slice(0, 8),
    stages,
    stageMax: Math.max(1, ...stages.map((step) => step.amount)),
    rows,
    regions,
    products,
    productMax: Math.max(1, ...products.map((row) => row.amount)),
    productAmount: products.reduce((total, row) => total + row.amount, 0),
    shippingFee: Number(data.afterSendShipping) || 0,
    lastClick,
    lastClickMax: Math.max(1, ...lastClick.map((row) => row.amount)),
    institutions,
    institutionTotals,
    institutionMax: Math.max(1, ...institutions.map((row) => row.sales)),
    timeline: data.afterSendTimeline || [],
  };
  if (target === 'group') {
    groupReport.value = view;
    productShowAll.value = false;
    institutionShowAll.value = false;
    hideTip();
    return;
  }
  singleView.value = view;
};

const loadGroupReport = async (names: string[]) => {
  if (!names.length) {
    groupReport.value = null;
    return;
  }
  busy.value = true;
  busyText.value = '선택한 폴더 종합 보고서를 만드는 중...';
  errorMessage.value = '';
  try {
    const res = await promoMailListApi.analyzeCampaigns(names);
    buildGroupReport(res.data?.data);
  } catch (error: any) {
    errorMessage.value =
      error.response?.data?.message || error.message || '종합 보고서를 만들지 못했습니다.';
  } finally {
    busy.value = false;
  }
};

const loadSavedStats = async () => {
  busy.value = true;
  busyText.value = '저장된 통계를 불러오는 중...';
  errorMessage.value = '';
  try {
    const res = await promoMailListApi.analyzeCampaigns();
    showSavedAnalysis(res.data?.data);
    statsLoaded.value = true;
  } catch (error: any) {
    errorMessage.value =
      error.response?.data?.message || error.message || '저장된 통계를 불러오지 못했습니다.';
  } finally {
    busy.value = false;
  }
};

const runGroupAnalysis = () => {
  if (!checkedFolders.value.length) return;
  loadGroupReport([...checkedFolders.value]);
};

watch(checkedFolders, () => {
  groupReport.value = null;
  hideTip();
});

const onStatsFolder = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const list = Array.from(input.files || []);
  input.value = '';
  if (!list.length) return;

  busy.value = true;
  busyText.value = '통계를 저장하는 중...';
  errorMessage.value = '';
  try {
    const files = await Promise.all(
      list.map(async (file) => ({
        name: file.name,
        relativePath: file.webkitRelativePath || file.name,
        text: await file.text(),
        size: file.size,
      }))
    );
    const folder = folderNameOf(files);
    const res = await promoMailListApi.saveCampaign(collectStibeeSavePayload(files, folder));
    showSavedAnalysis(res.data?.data);
    statsLoaded.value = true;
  } catch (error: any) {
    errorMessage.value = error.response?.data?.message || error.message || '통계 저장에 실패했습니다.';
  } finally {
    busy.value = false;
  }
};

const switchToMail = async () => {
  mainTab.value = 'mail';
  if (!mailLoaded.value) await loadStats();
};

const onFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0] || null;
  pendingFile.value = file;
  if (file && !uploadTitle.value.trim()) {
    uploadTitle.value = file.name.replace(/\.html?$/i, '');
  }
};

const uploadHtml = async () => {
  if (!pendingFile.value || !selectedSlot.value) return;
  busy.value = true;
  busyText.value = 'HTML 업로드 중...';
  errorMessage.value = '';
  try {
    await promoHtmlApi.upload(selectedSlot.value, pendingFile.value, uploadTitle.value);
    pendingFile.value = null;
    uploadTitle.value = '';
    if (fileInputRef.value) fileInputRef.value.value = '';
    await loadLandingSlots();
  } catch (error: any) {
    errorMessage.value =
      error.response?.data?.message || error.message || '업로드에 실패했습니다.';
  } finally {
    busy.value = false;
  }
};

const activateVersion = async (idx: number) => {
  if (!selectedSlot.value) return;
  busy.value = true;
  busyText.value = '게시 반영 중...';
  errorMessage.value = '';
  try {
    await promoHtmlApi.activate(selectedSlot.value, idx);
    await loadLandingSlots();
  } catch (error: any) {
    errorMessage.value =
      error.response?.data?.message || error.message || '게시에 실패했습니다.';
  } finally {
    busy.value = false;
  }
};

const downloadVersion = async (ver: PromoHtmlVersion) => {
  busy.value = true;
  busyText.value = '다운로드 중...';
  errorMessage.value = '';
  try {
    await promoHtmlApi.download(
      ver.slot,
      ver.idx,
      ver.originalName || `${ver.slot}-${ver.idx}.html`
    );
  } catch (error: any) {
    errorMessage.value = await blobErrorMessage(error, '다운로드에 실패했습니다.');
  } finally {
    busy.value = false;
  }
};

const removeVersion = async (ver: PromoHtmlVersion) => {
  const label = ver.title || ver.originalName || '이 버전';
  const extra = ver.isActive
    ? '\n지금 게시 중인 페이지입니다. 삭제하면 해당 주소는 준비 중으로 바뀝니다.'
    : '';
  if (!window.confirm(`「${label}」 버전을 삭제할까요?${extra}`)) return;
  busy.value = true;
  busyText.value = '삭제 중...';
  errorMessage.value = '';
  try {
    await promoHtmlApi.remove(ver.slot, ver.idx);
    await loadLandingSlots();
  } catch (error: any) {
    errorMessage.value =
      error.response?.data?.message || error.message || '삭제에 실패했습니다.';
  } finally {
    busy.value = false;
  }
};

onMounted(() => {
  if (mainTab.value === 'stats') loadSavedStats();
  nextTick(() => bindSectionObserver());
});

onUnmounted(() => {
  sectionObserver?.disconnect();
});

</script>

<style lang="scss" scoped>
@use '@/assets/styles/pages/promo-mail-list';
</style>
